// Web always uses relative paths; the webpack devServer proxies /ttauth/v1/*
// to TTAUTH_PROXY_TARGET (with cookieDomainRewrite stripping the upstream
// Set-Cookie Domain so the cookies are scoped to the SPA origin).
// In prod, the rider app must be served from the same origin as ttauth
// (or behind a reverse proxy that forwards /ttauth/v1/*).
//
// Native must hit ttauth directly (no proxy), so TTAUTH_BASE_URL in .env
// should be set to the absolute ttauth URL — used only by
// ttAuthService.native.ts; this file intentionally ignores it.
//
// DO NOT read process.env.TTAUTH_BASE_URL here — doing so would make every
// call use an absolute URL that bypasses the webpack proxy, causing the
// Set-Cookie Domain to be scoped to the TTAuth server instead of localhost.
// All paths MUST be relative so the devServer proxy intercepts them and
// rewrites Set-Cookie Domain to the SPA origin.
const TTAUTH_BASE_URL = '';

// ttauth splits its endpoints into cookie/* (browsers) and bearer/* (native).
// The refresh cookie is Path=/ttauth/v1/cookie/me, so the browser only
// sends it to that exact path.
export const ttAuthService = {
  loginRedirect(returnUrl?: string): void {
    // No trailing slash. Hosted UI naively concatenates `${appUrl}/ttauth/v1/...`
    // when it form-POSTs to /cookie/session; a trailing slash here ends up as
    // `https://host//ttauth/v1/...` which webpack's proxy context matcher
    // (/ttauth/v1) skips, falling through to historyApiFallback → index.html
    // and the round-trip silently breaks.
    const url = returnUrl ?? globalThis.location.origin;
    globalThis.location.href = `${TTAUTH_BASE_URL}/ttauth/v1/cookie/login-redirect?returnUrl=${encodeURIComponent(url)}`;
  },

  // Navigates the browser to the change-password hosted UI flow.
  // The auth service builds the Hosted UI URL (action=changePassword + appUrl
  // + optional email hint) — apps don't construct anything beyond this redirect.
  // Using a relative path lets the webpack proxy forward it transparently; the
  // auth service derives returnUrl from the request host when omitted.
  changePasswordRedirect(email?: string): void {
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    globalThis.location.href = `${TTAUTH_BASE_URL}/ttauth/v1/cookie/change-password-redirect${params}`;
  },

  logout(returnUrl?: string): void {
    const params = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
    globalThis.location.href = `${TTAUTH_BASE_URL}/ttauth/v1/cookie/logout${params}`;
  },

  // Refreshes the session by calling GET /ttauth/v1/cookie/me. The refresh
  // cookie is sent automatically; ttauth proactively issues a new session
  // cookie when the existing one is within 5 minutes of expiry.
  //
  // Returns { ok: true, token } when the session is alive.
  // token is the real JWT from the response body if the backend returns one,
  // or null when the backend only sets a cookie without a body token.
  // Returns { ok: false, token: null } when the session is expired or absent.
  // Refreshes the session by calling GET /ttauth/v1/cookie/me. The refresh
  // cookie is sent automatically; ttauth proactively issues a new session
  // cookie when the existing one is within 5 minutes of expiry.
  //
  // Returns { ok: true, token } when the session is alive.
  // token is the real JWT from the response body if the backend returns one,
  // or null when the backend only sets a cookie without a body token.
  // Returns { ok: false, token: null } ONLY when the server explicitly rejects
  // the session (HTTP non-2xx) — meaning the session is confirmed dead.
  //
  // Network/fetch errors (timeout, DNS failure, offline) are NOT caught here —
  // they propagate as thrown exceptions so the caller can distinguish between
  // "session confirmed expired" (ok: false) and "network unreachable, unknown"
  // (thrown). AppNavigator.web.tsx's bootstrap catches thrown errors
  // non-blocking and preserves the stored token, preventing false logouts
  // caused by transient network issues.
  async refresh(): Promise<{ ok: boolean; token: string | null }> {
    // NOTE: intentionally no top-level try/catch — let fetch/network errors
    // propagate so callers (ttAuthAdapter.getUser, AppNavigator bootstrap) can
    // distinguish transient failure from a confirmed expired session.
    const response = await fetch(`${TTAUTH_BASE_URL}/ttauth/v1/cookie/me`, {
      credentials: 'include',
    });
    if (!response.ok) {
      // Server explicitly rejected — session is confirmed dead.
      return { ok: false, token: null };
    }
    // TTAuth /cookie/me may return { data: { accessToken, expiresAt, ... } }
    // Try to extract the real JWT; fall back to null if absent (cookie-only flow).
    let token: string | null = null;
    try {
      const json = await response.json();
      token =
        json?.data?.accessToken ??
        json?.data?.token ??
        json?.accessToken ??
        json?.token ??
        null;
    } catch {
      /* response had no parseable JSON body — cookie-only flow, token stays null */
    }
    return { ok: true, token };
  },

  // Deletes the current user's GCIP / Firebase account via
  // DELETE /ttauth/v1/me. The session cookie is sent automatically via
  // credentials: 'include' — no token parameter required.
  //
  // Best-effort: always returns a boolean — never throws — so callers can
  // treat this as fire-and-forget without blocking the delete-account UX
  // when the auth-backend is momentarily unreachable.
  //
  // Call this BEFORE clearing local storage / logging out via ttAuth so that
  // the session cookie is still present and authenticates the request.
  async deleteAccount(): Promise<boolean> {
    try {
      const response = await fetch(`${TTAUTH_BASE_URL}/ttauth/v1/me`, {
        method: 'DELETE',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    }
  },

  // Returns expiresAt and nextRefreshAt from /cookie/me. Currently used only
  // by AuthLoader for sanity logging — the static /ttauth/v1/cookie/init.js
  // script (loaded via index.html) drives the actual proactive refresh schedule.
  // Re-uses refresh() to avoid a second network call; parses timestamps from the same response.
  async getSessionInfo(): Promise<{ expiresAt: Date | null; nextRefreshAt: Date | null }> {
    try {
      const response = await fetch(`${TTAUTH_BASE_URL}/ttauth/v1/cookie/me`, {
        credentials: 'include',
      });
      if (!response.ok) return { expiresAt: null, nextRefreshAt: null };
      const json = await response.json();
      return {
        expiresAt:     json?.data?.expiresAt     ? new Date(json.data.expiresAt)     : null,
        nextRefreshAt: json?.data?.nextRefreshAt ? new Date(json.data.nextRefreshAt) : null,
      };
    } catch {
      return { expiresAt: null, nextRefreshAt: null };
    }
  },
};